import * as React from "react";
import ReactTable, { Instance } from "react-table";
import { EditableCell } from "../react-table";
import "react-table/react-table.css";
import { Loan, LocalAuthority, PpiYearLoan } from "../../model";
import { FormattedNumber } from "react-intl";
import * as LoanDomain from "../../domain/sfp/loan-domain";
import * as Model from "../../model";
import { LocalAuthorityCrdYearService } from "../../service/sfp/local-authority-crd-year-service";
import * as ScenariiService from "../../service/sfp/scenarii-service";
import PPIYearService from "../../service/sfp/ppi-year-service";
import PPIService from "../../service/sfp/ppi-service";
import { LocalAuthorityDebtSimplifiedYearModel } from "../../model/local-authority-crd-year";
import { Link } from "react-router-dom";
import * as routes from "../../util/routes";

interface DebtRow {
  label: string;
  [key: string]: number | null | string | boolean | undefined;
  editableAllYears?: boolean;
  editableProspective?: boolean;
  /** Ligne reprise automatiquement de la dette détaillée : jamais saisissable. */
  readOnly?: boolean;
}

interface DebtSimplifiedState {
  data: DebtRow[];
  focusedCell?: { rowIndex: number; columnId: string };
  loadingCells: Record<string, boolean>;
  startYear: number,
  endYear: number,
  showNoPpiMessage?: boolean
}


export default class DebtSimplifiedComponent extends React.Component<DebtSimplifiedComponentProps, DebtSimplifiedState> {
  
  public reactTable: Instance<any>;  

  private currentYear = new Date().getFullYear();

  state: DebtSimplifiedState = {
    startYear: this.currentYear - 3,
    endYear: this.currentYear + 5,
    data: this.getInitialData(this.currentYear - 3, this.currentYear + 5),
    focusedCell: undefined,
    loadingCells: {},
    showNoPpiMessage: false,
  }; 

  private hasPpi(): boolean {
    return !!(this.props.ppi && this.props.ppi.id);
  }

  private generateYears(startYear: number, endYear: number): number[] {
    return Array.from(
      { length: endYear - startYear + 1 },
      (_, i) => startYear + i
    );
  }

  private getInitialData(startYear: number, endYear: number): DebtRow[] {
    
    const years = this.generateYears(startYear, endYear);

    // Lignes « emprunts simulés » : reprises de la ligne « Total dette simulée »
    // des onglets de la dette détaillée, jamais saisies.
    const simulatedTotals = LoanDomain.getSimulatedLoansTotalsByYear(
      this.props.loans || [],
      years
    );

    const empruntsEnEurosRetrospective =
      this.props.localAuthority.observations.filter(
        (o) => o.account && o.account.number === '1641' && o.year < this.currentYear && o.receipts === false
      );   

    const empruntsEnEurosProspective =
      this.props.ppiYearLoans.filter(
        (o) => o.year >= this.currentYear
    );           
        
    const remboursementEmpruntsReels = {
      key: FinancialRowKey.REMBOURSEMENT_EMPRUNTS_REELS,
      label: "Remboursement emprunts réels",
      ...years.reduce((acc, year) => {       
      const isRetrospective = year < this.currentYear; 
      if (isRetrospective) {
        // Retrospective
        const retro = empruntsEnEurosRetrospective.find(o => o.year === year);
        acc[year] = retro ? (retro.amount != null ? Number(retro.amount.toFixed(2)) : null) : null;        
      } else {
        // Prospective
        const prospective = empruntsEnEurosProspective.find(o => o.year === year);
        // Prospective simplifiée
        const ppiYear = this.props.ppi.years.find((o: any) => o.year === year);
        const prospectiveSimplifiee = ppiYear && ppiYear.loanDebtAmount != null ? ppiYear.loanDebtAmount : null;        
        const prospectiveDisplayed = prospectiveSimplifiee != null ? prospectiveSimplifiee : prospective && prospective.reimbourse != null ? Number(prospective.reimbourse.toFixed(2)) : null;
        acc[year] = prospectiveDisplayed;        
      }
      return acc;
      }, {} as Record<number, number | null>),
      editableProspective: true,
    };

    const remboursementEmpruntsSimules = {
      key: FinancialRowKey.REMBOURSEMENT_EMPRUNTS_SIMULES,
      label: "Remboursement emprunts simulés",
      ...this.mapYearsSimulated(years, simulatedTotals, "reimboursedCapital"),
      readOnly: true,
    };

    const account66111 = this.props.localAuthority.accounts.find((o:any) => o.account.number === '66111');
    const interetEcheanceRetrospective = this.props.localAuthority.observations.filter(
      (o) => o.account && o.account.number === '66111' && o.year < this.currentYear
    );     
    
    const buildInteretEcheanceProspective = this.buildLoans(years);    
    const interetEcheanceProspective = buildInteretEcheanceProspective.filter((o) => o.year >= this.currentYear);    

    const interetEmpruntsReels = {
      key: FinancialRowKey.INTERET_EMPRUNTS_REELS,
      label: "Intérêts emprunts réels",
      ...years.reduce((acc, year) => {
        const isRetrospective = year < this.currentYear;

        // Valeur enregistrée dans la table de dette simplifiée (scenarii)
        const yearData = this.props.scenarii && this.props.scenarii.years ? this.props.scenarii.years.find((o: any) => o.year === year) : null;
        let valeurSimplifiee = null;
        if (yearData && yearData.accounts && yearData.accounts.length > 0 && account66111) {
          const data = yearData.accounts.find((o: any) => o.receipts === account66111.account.receipts && o.chapter_id === account66111.chapter.id);
          if (data) {
            valeurSimplifiee = data.amount;
          }
        }

        if (isRetrospective) {
          // Règle : priorité à la valeur de la dette simplifiée, sinon constat rétrospectif
          if (valeurSimplifiee != null) {
            acc[year] = valeurSimplifiee;
          } else {
            const retro = interetEcheanceRetrospective.find((o) => o.year === year);
            acc[year] = retro ? (retro.amount != null ? Number(retro.amount.toFixed(2)) : 0) : 0;
          }
        } else {
          // Prospective
          const prospective = interetEcheanceProspective.find((o) => o.year === year);
          const prospectiveDisplayed = valeurSimplifiee != null ? valeurSimplifiee : prospective ? Number(prospective.interests.toFixed(2)) : 0;
          acc[year] = prospectiveDisplayed;
        }
        return acc;
      }, {} as Record<number, number | null>),
      editableProspective: true,
    };

        
    const encoursDeLaDetteRow = {
      key: FinancialRowKey.CRD_EN_COURS_DE_LA_DETTE,
      label: "CRD - En cours de dette",
      ...this.mapYearsProperty.call(this, years, "amount"),
      editableAllYears: true,
    };

    const empruntsSimulesCrd = {
      key: FinancialRowKey.CRD_EMPRUNTS_SIMULES,
      label: "CRD - Emprunts simulés",
      ...this.mapYearsSimulated(years, simulatedTotals, "remainingCapital"),
      readOnly: true,
    };

    const interetsEmpruntsSimules = {
      key: FinancialRowKey.INTERETS_EMPRUNTS_SIMULES,
      label: "Intérêts emprunts simulés",
      ...this.mapYearsSimulated(years, simulatedTotals, "interest"),
      readOnly: true,
    };

    return [
      encoursDeLaDetteRow,
      empruntsSimulesCrd,
      remboursementEmpruntsReels,
      remboursementEmpruntsSimules,
      interetEmpruntsReels,
      interetsEmpruntsSimules
    ];
  }


  render() {    
    const { data, startYear, endYear } = this.state;
    const years = this.generateYears(startYear, endYear);

    return (
      <div>
        {this.state.showNoPpiMessage && (
          <div
            className="alert alert-warning"
            style={{
              margin: "8px 0",
              padding: "10px 14px",
              border: "1px solid #f0ad4e",
              borderRadius: 4,
              backgroundColor: "#fcf8e3",
              color: "#8a6d3b",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span>
              Aucun PPI n'existe pour cette collectivité. La modification du
              « Remboursement emprunts réels » nécessite un PPI.{" "}
              <Link to={`/${this.props.localAuthority.slug}${routes.PPI}`}>
                Accéder à la page PPI
              </Link>
            </span>
            <button
              type="button"
              style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, color: "#8a6d3b" }}
              onClick={() => this.setState({ showNoPpiMessage: false })}
              aria-label="Fermer"
            >
              ×
            </button>
          </div>
        )}
        <div className="filtreDateContainer">
          <div className="inputContainer">
            <label className="label label-start" htmlFor="start-year">Année de début :</label>
            <input
              id="start-year"
              className="input"
              type="number"
              value={startYear}
              min={endYear - 30}
              max={endYear - 1}
              onChange={(e) => this.onYearChange(Number(e.target.value), endYear)}
            />
          </div>

          <div className="inputContainer">
            <label className="label label-end" htmlFor="end-year">Année de fin :</label>
            <input
              id="end-year"
              className="input"
              type="number"
              value={endYear}
              min={startYear + 1}
              max={startYear + 30}
              onChange={(e) => this.onYearChange(startYear, Number(e.target.value))}
            />
          </div>
        </div>
        <div style={{ height: "100%", overflow: "auto", width: "100%" }}>
          <ReactTable
            columns={this.getColumns(years)}
            collapseOnDataChange={false}
            data={data}
            showPagination={false}
            pageSize={data.length}
            ref={(r: Instance<any>) => (this.reactTable = r)}
            className="-striped -highlight"
          />
        </div>
      </div>
    );
  }


  private mapYearsProperty<T extends keyof LocalAuthorityDebtSimplifiedYearModel>(
    years: number[],
    property: T
  ): Record<number, number | null> {
    return years.reduce((acc, year) => {
      let crdyear: LocalAuthorityDebtSimplifiedYearModel | undefined = undefined; 
      if (this.props.localAuthority.localAuthorityDebtSimplifiedYears) { 
        crdyear = this.props.localAuthority.localAuthorityDebtSimplifiedYears.find(o => o.year === year); 
      }
      acc[year] = crdyear ? crdyear[property] : null;
      return acc;
    }, {} as Record<number, number | null>);
  }

  /**
   * Prochain index de ligne saisissable dans la direction donnée, en sautant les
   * lignes reprises automatiquement. Renvoie l'index de départ si aucune n'est trouvée.
   */
  private findEditableRowIndex(fromIndex: number, direction: number): number {
    const data = this.state.data;
    let i = fromIndex + direction;
    while (i >= 0 && i < data.length) {
      if (data[i].readOnly !== true) return i;
      i += direction;
    }
    return fromIndex;
  }

  private mapYearsSimulated(
    years: number[],
    totalsByYear: LoanDomain.SimulatedLoanTotalsByYear,
    property: keyof LoanDomain.SimulatedLoanTotals
  ): Record<number, number> {
    return years.reduce((acc, year) => {
      acc[year] = LoanDomain.getSimulatedLoanTotals(totalsByYear, year)[property];
      return acc;
    }, {} as Record<number, number>);
  }

  private getColumns(years: number[]) {
    return [
      {
        Header: "Libellé",
        accessor: "label",
        minWidth: 260,
      },
      {
        Header: "Année",
        columns: this.getYearsColumns(years),
      },
    ];
  }

  private getYearsColumns(years: number[]) {
    const bluecolor = "#35b1c6";
    return years.map((y) => ({
      Header: y,
      headerStyle: { color: bluecolor },
      className: "text-right",
      accessor: y.toString(),
      Cell: (cellInfo: any) => {   
        const isProspective = Number(cellInfo.column.Header) >= this.currentYear;
        const editableAll = cellInfo.original.editableAllYears === true;
        const editableFuture = cellInfo.original.editableProspective === true && isProspective;

        const value = cellInfo.value;
        const numericValue = value !== null && value !== undefined ? value : 0;
        const displayedValue = <span>{this.getCurrencyNumber(numericValue)}</span>;

        // Lignes reprises automatiquement des emprunts simulés de la dette détaillée.
        if (cellInfo.original.readOnly === true) {
          return (
            <span
              className="editable-cell disabled"
              style={{ cursor: "not-allowed", opacity: 0.6 }}
              title="Valeur reprise automatiquement de la ligne « Total dette simulée » de la dette détaillée"
            >
              {displayedValue}
            </span>
          );
        }

        // Cas particulier : "Remboursement emprunts réels" prospectif nécessite un PPI.
        // Si aucun PPI n'existe, on désactive l'édition et on affiche un message.
        const isRemboursementReels = cellInfo.original.key === FinancialRowKey.REMBOURSEMENT_EMPRUNTS_REELS;
        if (isRemboursementReels && isProspective && !this.hasPpi()) {
          return (
            <span
              className="editable-cell disabled"
              style={{ cursor: "not-allowed", opacity: 0.6 }}
              onClick={() => this.setState({ showNoPpiMessage: true })}
            >
              {displayedValue}
            </span>
          );
        }

        // Seuls les drapeaux de la ligne décident de l'édition : un `isProspective` en
        // tête rendrait éditable toute cellule d'année >= année courante, y compris
        // sur les lignes reprises automatiquement.
        if (editableFuture || editableAll) {

          const focused = this.state.focusedCell
            ? this.state.focusedCell.rowIndex === cellInfo.index &&
              this.state.focusedCell.columnId === cellInfo.column.id
            : false;       

          const id = `debt-${cellInfo.index}-${cellInfo.column.id}`;
          const maxRow = this.state.data.length - 1;
          const maxCol = years.length - 1;

          const colIndex = years.indexOf(y);
          const rowIndex = cellInfo.index;

          // Les lignes reprises automatiquement ne sont pas focusables : on les saute.
          const upCell = { rowIndex: this.findEditableRowIndex(rowIndex, -1), columnId: y.toString() };
          const downCell = { rowIndex: this.findEditableRowIndex(rowIndex, 1), columnId: y.toString() };

          let rightCell;
          if (colIndex < maxCol) {
            rightCell = { rowIndex, columnId: years[colIndex + 1].toString() };
          } else if (rowIndex < maxRow) {
            rightCell = { rowIndex: this.findEditableRowIndex(rowIndex, 1), columnId: years[0].toString() };
          } else {
            rightCell = null;
          }

          let leftCell;
          if (colIndex > 0) {
            leftCell = { rowIndex, columnId: years[colIndex - 1].toString() };
          } else if (rowIndex > 0) {
            leftCell = { rowIndex: this.findEditableRowIndex(rowIndex, -1), columnId: years[maxCol].toString() };
          } else {
            leftCell = null;
          }                    

          return (
            <EditableCell
              displayedValue={displayedValue}
              focused={focused}
              checked={true}
              id={id}
              inputType="number"
              loading={(this.state.loadingCells && this.state.loadingCells[`${rowIndex}-${cellInfo.column.id}`]) || false}
              name={id}
              value={numericValue}
              onPressDown={(newValue: number) =>
                this.saveAndFocus(
                  cellInfo.index,
                  cellInfo.column.id,
                  newValue,
                  downCell
                )
              }
              onPressEnter={(newValue: number) =>
                this.saveAndFocus(
                  cellInfo.index,
                  cellInfo.column.id,
                  newValue,
                  downCell,
                )
              }
              onPressEscape={() =>
                this.handleCellPressEscape(
                  cellInfo.index,
                  cellInfo.column.id,
                  numericValue
                )
              }
              onPressLeft={(newValue: number) =>
                this.saveAndFocus(
                  cellInfo.index,
                  cellInfo.column.id,
                  newValue,
                  leftCell
                )
              }
              onPressRight={(newValue: number) =>
                this.saveAndFocus(
                  cellInfo.index,
                  cellInfo.column.id,
                  newValue,
                  rightCell
                )
              }
              onPressShiftTab={(newValue: number) =>
                this.saveAndFocus(
                  cellInfo.index,
                  cellInfo.column.id,
                  newValue,
                  leftCell              
                )
              }
              onPressTab={(newValue: number) =>
                this.saveAndFocus(
                  cellInfo.index,
                  cellInfo.column.id,
                  newValue,
                  rightCell
                )
              }
              onPressUp={(newValue: number) =>
                this.saveAndFocus(
                  cellInfo.index,
                  cellInfo.column.id,
                  newValue,
                  upCell
                )
              }
              min={0.0}
            />
          );
        } else {
          return displayedValue;
        }
      },
    }));
  }

  private getCurrencyNumber = (value: any) => (
    <FormattedNumber currency="EUR" style="currency" value={value} />
  );

  private onYearChange = (newStart: number, newEnd: number) => {
    if (newStart < 2000) newStart = 2000;
    if (newEnd > 2100) newEnd = 2100;

    // start doit rester inférieur à end
    if (newStart >= newEnd) newStart = newEnd - 1;
    if (newEnd <= newStart) newEnd = newStart + 1;

    this.setState({
      startYear: newStart,
      endYear: newEnd,
      data: this.getInitialData(newStart, newEnd)
    });
  };
  
  private saveAndFocus = (rowIndex: number, columnId: string, newValue: number, nextCell: { rowIndex: number; columnId: string } | null): void => {
      const year = Number(columnId);
      const row = this.state.data[rowIndex];
      const numericValue = newValue != null && newValue !== '' as any ? Number(newValue) : 0;

      // Comparer avec l'ancienne valeur
      const oldValue = row[columnId];      
      if (oldValue === numericValue) {
        this.setState(prevState => ({
          data: prevState.data, // data inchangé
          focusedCell: nextCell != null ? nextCell : prevState.focusedCell,
        }));
        return; // pas d'appel API
      }

      // activer loader
      this.isLoading(true, rowIndex, columnId);
      // appel API
      this.saveToBackend(rowIndex, row, columnId, year, numericValue, nextCell);
  }

  private saveToBackend = async (
      rowIndex: number,
      row: any,
      columnId: string,
      year: number,
      value: number,
      nextCell: { rowIndex: number; columnId: string } | null  
  ): Promise<void> => {
    try {
      const payload: Partial<LocalAuthorityDebtSimplifiedYearModel> = { year };
      let result: any;
      switch (row.key) {
        case FinancialRowKey.CRD_EN_COURS_DE_LA_DETTE:
            payload.amount = value;
            const response1: any = await LocalAuthorityCrdYearService.upsert(this.props.localAuthority.id, payload);
            result = response1.status;
            if (result) this.props.onDebtSimplifiedYearUpdate(year, { amount: value });
          break;

        // Les lignes « emprunts simulés » sont en lecture seule : elles sont reprises
        // des emprunts simulés de la dette détaillée et ne sont plus persistées.

        case FinancialRowKey.REMBOURSEMENT_EMPRUNTS_REELS:
            // Sécurité : sans PPI, on ne tente aucun appel API (évite d'envoyer undefined).
            if (!this.hasPpi()) {
              this.setState({ showNoPpiMessage: true });
              this.isLoading(false, rowIndex, columnId);
              return;
            }
            const targetYear = year;
            const existingYears = this.props.ppi.years.map((y: any) => y.year);
            const lastYear = existingYears.length > 0 ? Math.max(...existingYears) : targetYear - 1;

            // créer les années manquantes
            if (targetYear > lastYear) {
              for (let y = lastYear + 1; y <= targetYear; y++) {
                await PPIService.addYear(this.props.ppi.id);
              }
              // rafraîchir le PPI après création
              await this.props.refreshPpi();
            }

            // récupérer l'année cible
            const ppiYear = this.props.ppi && this.props.ppi.years ? this.props.ppi.years.find((o: any) => o.year === targetYear) : null;
        
            // update
            if (ppiYear) {
              result = await PPIYearService.updatePPIYear(ppiYear.id, 'loanDebtAmount', value);
              await this.props.refreshPpi();
            }          
          break;

        case FinancialRowKey.INTERET_EMPRUNTS_REELS:
          const acc = this.props.localAuthority.accounts.find(o => o.account.number == '66111');
          if (acc) {
            const res = await ScenariiService.updateValue(
              this.props.scenarii,
              year,
              acc.account,
              value,
              false,
              false
            );
            await this.props.refreshScenarii();
            result = res != null;
          }
          break;
        default:
      }
      
      if (result) {
        // succès → mise à jour valeur
        this.setState(prevState => {
  
          const data = [...prevState.data];
          data[rowIndex] = {
            ...data[rowIndex],
            [year]: value,
          };
  
          return {
            data,
            focusedCell: nextCell,
          };
        });
      }
    } catch (error) {
      console.error("Erreur sauvegarde BDD", error);
    }
    this.isLoading(false, rowIndex, columnId);
  };

  private isLoading = (show: boolean, rowIndex: number, columnId: string): void => {
    this.setState(prevState => ({
      loadingCells: {
        ...prevState.loadingCells,
        [`${rowIndex}-${columnId}`]: show,
      }
    }));
  };  

  private handleCellPressEscape = (rowIndex: number, columnId: string, oldValue: number): void => {
    const data = [...this.state.data];
    data[rowIndex][columnId] = oldValue; // rollback de la valeur
    this.setState({ data, focusedCell: undefined });  
  };  

  private buildLoans(years: number[]): Model.PpiYearLoan[] {
      const lds = LoanDomain.getLoansWithDecomposition(this.props.loans);

      let yearLoans: Model.PpiYearLoan[] = [],
          yearLoan: Model.PpiYearLoan;

      lds.forEach((ld) => {
          const decomposition = ld.decomposition;
          let decom = {
              interests: [],
          };
          decomposition.paymentDates.map((date, i) => {
              if (decomposition.remainingCapitals[i] > 0) {
                  decom.interests.push(decomposition.interests[i]);
              }
          });
          years.forEach((year) => {
              yearLoan = yearLoans.find((yl) => yl.year == year);
              if (!yearLoan) {
                  yearLoan = {
                      year: year,
                      total: 0,
                      reimbourse: 0,
                      interests: 0,
                      remaining: 0,
                      reimbours: 0,
                  };
                  yearLoans.push(yearLoan);
              }

              if (isNaN(yearLoan.interests)) yearLoan.interests = 0;
              yearLoan.interests += LoanDomain.getYearInterest(decomposition, year);

          });
      });

      return yearLoans;
  }
  
}

export enum FinancialRowKey {
  CRD_EN_COURS_DE_LA_DETTE = 'CRD_EN_COURS_DE_LA_DETTE',
  CRD_EMPRUNTS_SIMULES = 'CRD_EMPRUNTS_SIMULES',
  REMBOURSEMENT_EMPRUNTS_REELS = 'REMBOURSEMENT_EMPRUNTS_REELS',
  REMBOURSEMENT_EMPRUNTS_SIMULES = 'REMBOURSEMENT_EMPRUNTS_SIMULES',
  INTERET_EMPRUNTS_REELS = 'INTERET_EMPRUNTS_REELS',
  INTERETS_EMPRUNTS_SIMULES = 'INTERETS_EMPRUNTS_SIMULES'
}

interface DebtSimplifiedComponentProps {
  years: number[];
  checked?: boolean;
  focusedCell?: any;
  consulted?: boolean;
  localAuthority: LocalAuthority;
  ppiYearLoans: PpiYearLoan[];
  loading: boolean;
  loans: Loan[];
  scenarii:any;
  ppi:any;
  refreshPpi(): any;
  refreshScenarii(): any;
  onDebtSimplifiedYearUpdate(year: number, payload: Partial<LocalAuthorityDebtSimplifiedYearModel>): void;
}



// WEBPACK FOOTER //
// ./src/component/debt/debt-simplified-component.tsx